/**
 * MODULE: platform/browser/frontend/components/BrowserCommandDock.tsx
 * PURPOSE: Collapsed command strip — matches shell assistant side-rail composer styling.
 * PUBLIC_API: BrowserCommandDock
 * RECENT: 2026-06-18 — Side-rail visual parity with BrowserAssistantComposer.
 */

import { ASSISTANT_RAIL_BG_CLASS } from '@design-system/components/AiAssistant/assistantPageTheme';
import { BrowserAssistantComposer } from './BrowserAssistantComposer';

interface BrowserCommandDockProps {
  input: string;
  inputPlaceholder: string;
  isDisabled: boolean;
  isStreaming: boolean;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onExpandPanel: () => void;
  onCancelStream?: () => void;
}

export function BrowserCommandDock({
  input,
  inputPlaceholder,
  isDisabled,
  isStreaming,
  onInputChange,
  onSend,
  onExpandPanel,
  onCancelStream,
}: BrowserCommandDockProps) {
  return (
    <div
      className={`flex shrink-0 flex-col gap-2 border-t border-gray-200/80 px-3 py-2 dark:border-gray-700/60 sm:px-4 ${ASSISTANT_RAIL_BG_CLASS}`}
      data-testid="browser-command-dock"
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onExpandPanel}
          className="shrink-0 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-white dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          data-testid="browser-expand-command-panel"
          title="Show full command panel and action log"
        >
          Commands
        </button>
        {isStreaming ? (
          <span
            className="text-xs text-primary-800 dark:text-primary-100"
            data-testid="browser-agent-working-dock"
          >
            Agent is working…
          </span>
        ) : null}
      </div>
      <BrowserAssistantComposer
        value={input}
        onChange={onInputChange}
        onSend={onSend}
        disabled={isDisabled}
        isStreaming={isStreaming}
        placeholder={inputPlaceholder}
        onCancelStream={onCancelStream}
        compact
      />
    </div>
  );
}
